import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  FormGroup,
  Label,
  Spinner,
  Table,
  Button,
  Badge,
} from "reactstrap";
import { withRouter, Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getTableAnalytics, exportTableAnalytics } from "../../Endpoint/Admin";
import { downloadBase64File } from "../../utils/exportToExcel";
import { toast } from "react-toastify";

class TableAnalytics extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      analyticsData: null,
      breadcrumbItems: [
        { title: "Analytics", link: "/analytics" },
        { title: "Table Analytics", link: "#" },
      ],
    };
  }

  getDefaultStartDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  };

  getDefaultEndDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  componentDidMount() {
    this.loadAnalytics();
  }

  loadAnalytics = async () => {
    this.setState({ loading: true });
    const { startDate, endDate } = this.state;

    const response = await getTableAnalytics({ startDate, endDate });
    this.setState({
      analyticsData: response?.success ? response.data : null,
      loading: false,
    });
  };

  handleDateChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleApplyFilters = () => {
    this.loadAnalytics();
  };

  handleExport = async () => {
    this.setState({ exporting: true });
    try {
      const { startDate, endDate } = this.state;
      const res = await exportTableAnalytics({ startDate, endDate });
      if (res?.success && res.data) {
        downloadBase64File(res.data.base64, res.data.fileName);
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      toast.error("Export failed");
    }
    this.setState({ exporting: false });
  };

  formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  getDayName = (dayNum) => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return days[dayNum] || `Day ${dayNum}`;
  };

  render() {
    const { loading, startDate, endDate, analyticsData } = this.state;

    const summary = analyticsData?.summary || {};
    const reservationsByDay = analyticsData?.reservationsByDay || [];
    const reservationsByPartySize = analyticsData?.reservationsByPartySize || [];
    const popularTables = analyticsData?.popularTables || [];
    const noShowRate = analyticsData?.noShowRate || 0;

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Table Analytics" breadcrumbItems={this.state.breadcrumbItems} />

            {/* Filters */}
            <Card>
              <CardBody>
                <Row className="align-items-end">
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        name="startDate"
                        value={startDate}
                        onChange={this.handleDateChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        name="endDate"
                        value={endDate}
                        onChange={this.handleDateChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <Button color="primary" onClick={this.handleApplyFilters}>
                      Apply
                    </Button>
                    <Button color="success" className="ml-2" disabled={this.state.exporting} onClick={this.handleExport}>
                      {this.state.exporting ? <Spinner size="sm" className="mr-1" /> : <i className="ri-file-excel-2-line mr-1"></i>} Export XLS
                    </Button>
                  </Col>
                  <Col md={4} className="text-right">
                    <Link to="/analytics" className="btn btn-outline-secondary">
                      Back to Dashboard
                    </Link>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Loading table analytics...</p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <Row>
                  <Col md={3}>
                    <Card className="bg-success text-white">
                      <CardBody>
                        <h6 className="text-white-50">Total Reservations</h6>
                        <h3>{this.formatNumber(summary.totalReservations)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-primary text-white">
                      <CardBody>
                        <h6 className="text-white-50">Completed</h6>
                        <h3>{this.formatNumber(summary.completedReservations)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-info text-white">
                      <CardBody>
                        <h6 className="text-white-50">Avg Party Size</h6>
                        <h3>{(summary.avgPartySize || 0).toFixed(1)} guests</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-danger text-white">
                      <CardBody>
                        <h6 className="text-white-50">No-Show Rate</h6>
                        <h3>{noShowRate.toFixed(1)}%</h3>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  {/* Reservations by Day */}
                  <Col md={6}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">Reservations by Day</h4>
                        {reservationsByDay.length === 0 ? (
                          <p className="text-muted text-center">No data available</p>
                        ) : (
                          <Table className="table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Day</th>
                                <th className="text-right">Reservations</th>
                                <th className="text-center">Popularity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reservationsByDay.map((item, index) => {
                                const maxCount = Math.max(...reservationsByDay.map(d => d.count), 1);
                                const percentage = (item.count / maxCount) * 100;
                                return (
                                  <tr key={index}>
                                    <td>{this.getDayName(item._id)}</td>
                                    <td className="text-right">{this.formatNumber(item.count)}</td>
                                    <td className="text-center">
                                      <div className="progress" style={{ height: "8px" }}>
                                        <div
                                          className="progress-bar bg-success"
                                          style={{ width: `${percentage}%` }}
                                        ></div>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Reservations by Party Size */}
                  <Col md={6}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">Reservations by Party Size</h4>
                        {reservationsByPartySize.length === 0 ? (
                          <p className="text-muted text-center">No data available</p>
                        ) : (
                          <Table className="table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Party Size</th>
                                <th className="text-right">Count</th>
                                <th className="text-center">Distribution</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reservationsByPartySize.map((item, index) => {
                                const total = reservationsByPartySize.reduce((sum, p) => sum + p.count, 0);
                                const percentage = total > 0 ? (item.count / total) * 100 : 0;
                                return (
                                  <tr key={index}>
                                    <td>
                                      <Badge color="info">{item._id} guests</Badge>
                                    </td>
                                    <td className="text-right">{this.formatNumber(item.count)}</td>
                                    <td className="text-center">
                                      <small>{percentage.toFixed(1)}%</small>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Popular Tables */}
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Popular Tables</h4>
                    {popularTables.length === 0 ? (
                      <p className="text-muted text-center">No data available</p>
                    ) : (
                      <Table className="table-sm mb-0">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Table</th>
                            <th className="text-center">Capacity</th>
                            <th className="text-right">Total Reservations</th>
                          </tr>
                        </thead>
                        <tbody>
                          {popularTables.slice(0, 10).map((table, index) => (
                            <tr key={table._id}>
                              <td>{index + 1}</td>
                              <td>{table.name || `Table #${table._id?.toString().slice(-4)}`}</td>
                              <td className="text-center">
                                <Badge color="secondary">{table.capacity || '-'} seats</Badge>
                              </td>
                              <td className="text-right">{this.formatNumber(table.reservations)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </>
            )}
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(withTranslation()(TableAnalytics));
